import _ from "lodash";
import {
  createSelector,
  createStructuredSelector,
} from "reselect";
import {
  defaultSort,
  generateFilters,
  injectsFromSchema,
  makeGetId,
  schemaFilter,
  schemaIndexText,
  schemaSort,
} from "CommonCore/schema-util";

/**
 * @summary Factory für Änderungen zum Redux-Store
 * @desc
 * Bei Redux soll standardmäßig der Entwickler mehrere sogenannte Actions (Handlungen) festlegen
 * und deren Wirkung auf dem Store durch Reducers ausarbeiten. Stattdessen gibt es in diesem
 * Projekt nur eine Art von Actions und nur einen Reducer - inline. Eine Inline-Handlung
 * ist aus zwei Teile gebaut: eine Beschreibung der geplannten Änderungen zum Store und
 * eine Funktion die die Änderungen anwendet.
 *
 * Die Funktion kriegt als erstes Argument das ganze Redux-State. Die Funktion darf
 * beliebige Änderungen zum State machen, sofern sie alle beschieben wurden. Die Beschreibung
 * steuert welche Felder aus dem Store im Voraus geklont werden müssen. Alle Änderungen,
 * die nicht beschrieben wurden, könnten dazu führen, dass die Oberfläche die Änderungen
 * nicht kriegt.
 *
 * Die Beschreibung hat ein unter die folgende Formate:
 * * String - Eine tiefe Kopie des mit dem gleichen Name Felds gemacht wird, bevor
 *    die Anwendungsfunktionen aufgerufen wird.
 * * String[] - Wie beim einzelnen String, eine tiefe Kopie jeweils gemacht wird.
 * * Objekt - Jedes als Schlüssel genannte Feld wird verarbeitet hinsichtlich des
 *    zugehorigen Werts.
 *
 * @param  {String | Inline-Spec} modifies Die Beschreibung der geplannten Änderungen
 * @param  {Function} apply Die die Änderungen anwendende Funktion
 * @return {Redux-Action}
 *
 * @example
 * // tiefe Kopie von state.foo wird im Voraus gemacht
 * inline('foo', state => state.foo.bar = 123);
 *
 * // tiefe Kopie von state.foo und state.bar
 * inline(['foo', 'bar'], state => ...)
 *
 * inline({
 *  foo: 'discard', // Der alte Wert wird vom State gelöscht (als Optimierung)
 *  bar: 'deep', // Der alte Wert wird tiefkopiert (die sicherste Variante)
 *  baz: 'shallow' // Der alte Wert wird flachkopiert (als Optimierung)
 *  flub: 'deep+check' // Der alte Wert wird tiefkopiert (als Optimierung)
 * }, state => {
 *   // state.foo ist hier undefined
 *   // state.bar ist eine tiefe Kopie des alten Werts
 *   // state.baz ist eine flache Kopie des alten Werts
 *
 *   // state.flub ist eine tiefe Kopie des alten Werts.
 *   // Nach dem Aufruf der Funktion wird der neue Wert mit dem alten Wert verglichen
 *   // und falls keine Änderungen wirklich vorhanden sind, wird die Kopie
 *   // ruckgängig gemacht, also der alte Wert wird im Store zurück zugewiesen.
 * })
 */
export function inline(modifies, apply) {
  const stack = Error().stack;
  return {
    type: `INLINE:${ _.isString(modifies) ? modifies : _.keys(modifies).join(",") }`,
    modifies,
    apply,
    stack: stack && stack.split("\n"),
  };
}

/**
 * @summary [UI-Liste]{@link module:UI-Liste} ansehen.
 *
 * @param  {String} name Name der UI-Liste.
 * @param  {Schema} schema Das {@link Schema} der Eingabe-Liste
 * @param  {Selector} inputSelector Der Eingabeliste-Selector, oder undefined wenn die Liste mit dem gleichen
 *   Name aus dem Store genommen werden soll.
 * @param  {Selector} inflightSelector Der Inflight-Selector oder undefined.
 * @return {Selector} Der UI-Liste-Selector. Wenn die Liste 'fooList' heißt, so wird beim
 * Inject die Eigenschaft uiFooList zur Verfügung gestellt. Deren Wert kann unmittelbar
 * einem EntityTable gegeben werden.
 *
 * @example
 * const someUiListSelector = createUiListSelector('someList', someSchema, someInput);
 * \@store(someUiListSelector)
 * class SomeComponent extends Component {
 *  render() {
 *    const {
 *      items,             // Array fon gefilterte, sortierte, aus someInput genommene Elemente
 *      itemsPerPage,      // Menge der pro Seite zu zeigenden Datensätze (Paginierung)
 *      page,              // Index der aktuell zu zeigenden Seite (Paginierung)
 *      filter,            // Suche-Daten, werden vom schemaFilter benutzt
 *      sort,              // Sortierungs-Daten, werden vom schemaSort benutzt
 *      columnConfig,      // Set der ein-/ausgeblendeten Spalten
 *      userFilters,       // gespeicherte Suchen
 *      selection,         // Selektierte Datensätze für Mehrfachaktionen
 *      currentUserFilter, // aktuell ausgewählte, früher gespeicherte Suche
 *      inflight,          // true wenn die Eingabe-Liste noch nicht verfügbar ist (gemäß dem Inflight-Selektor)
 *    } = this.props.uiSomeList
 *  }
 * }
 */
export function createUiListSelector(name, schema, inputSelector, inflightSelector) {
  if (schema.table && !schema.sorting) {
    schema.sorting = _.keyBy(schema.table.filter(s => s.sortable), "field");
  }

  // Filter generieren/anreichern
  if (schema.table && (!schema.filters || schema.appendFilters)) {
    generateFilters(schema);
  }

  const uiName = `ui${ _.upperFirst(name) }`;
  const injects = injectsFromSchema(schema);
  const getId = makeGetId(schema.id);
  if (!getId) {
    console.warn(`UI list ${ name } has no schema.id`);
  }

  const stateSelector = createStructuredSelector(_.fromPairs(injects.map(p => [p, state => _.get(state, p)])));
  const effectiveInputSelector = inputSelector || (state => state[name]);

  const searchIndexSelector = createSelector(
    effectiveInputSelector,
    stateSelector,
    (collection, state) => getId && schemaIndexText(collection, schema.filters, state),
  );

  const filteredDataSelector = createSelector(
    searchIndexSelector,
    inputSelector || (state => state[name]),
    stateSelector,
    state => state.ui && state.ui[uiName] && state.ui[uiName].filter,
    (index, collection, state, filter) => {
      const filteredList = schemaFilter(index, collection, filter, schema.filters, getId, state);
      return _.keyBy(filteredList, getId);
    },
  );

  const sortedDataSelector = createSelector(
    inputSelector || (state => state[name]),
    state => state.ui && state.ui[uiName] && state.ui[uiName].sort,
    stateSelector,
    (inputList, sortBy, state) => {
      const sortedList = sortBy
        ? schemaSort(inputList, sortBy, schema.sorting, state)
        : inputList;
      return _.map(sortedList, getId);
    },
  );

  const listDataSelector = createSelector(
    effectiveInputSelector,
    filteredDataSelector,
    sortedDataSelector,
    state => state.ui && state.ui[uiName],
    state => state[name + "ItemCount"],
    inflightSelector || (state => state[`${ name }Inflight`]),
    stateSelector,

    (inputList, filteredLookup, sortedList, listData, reportedItemCount, inflight) => {
      /*
       * TODO: optimization - move the pagination into the ui list selector
       * (window over sortedList, then lookup into filteredLookup, remove from EntityTable).
       * One thing to look after is that some components (like LehreinheitVorgabeDeputatPage)
       * do not use EntityTable and therefore do not paginate at all.
       * Maybe make it so that lists are computed whole when they have less than 100
       * items and EntityTable handles pagination and paginated here when they have
       * more than 100 items and EntityTable shows everything as is.
       */
      listData = _.clone(listData) || {};
      listData.items = reportedItemCount ? inputList : _.map(sortedList, id => filteredLookup[id]).filter(_.identity);
      listData.inflight = inflight;
      listData.itemsPerPage = listData.itemsPerPage || 10;
      listData.page = listData.page || 0;
      if (reportedItemCount) {
        listData.reportedItemCount = reportedItemCount;
      }

      return listData;
    },
  );

  const selector = derived(
    uiName,
    listDataSelector,
    createSelector(
      (state, props) => props,
      props => ({
        loadUiListSettings: props.loadUiListSettings,
        setUiListSortKeyIfEmpty: props.setUiListSortKeyIfEmpty,
        setUiListFilterIfEmpty: props.setUiListFilterIfEmpty,
        setUiListSortKey: props.setUiListSortKey,
      }),
    ),
    (listData, actions) => {
      setTimeout(() => {
        actions.loadUiListSettings(name);
        actions.setUiListSortKeyIfEmpty(name, () => defaultSort(schema));

        if (schema.defaultFilters) {
          actions.setUiListFilterIfEmpty(name, schema.defaultFilters);
        }
      });

      return listData;
    },
  );
  return selector;
}

export function asyncOperation(modifiedKey, body, cacheKey) {
  return async function(dispatch, getState) {
    const inflightKey = `${ modifiedKey }Inflight`;
    const inflightCacheKey = cacheKey && `${ modifiedKey }InflightCache`;
    const state = getState();
    if (state[inflightKey]) {
      if (!cacheKey) {
        console.warn(`Operation on ${ modifiedKey } is in flight.`);
      }
      if (!cacheKey || _.get(state, [inflightCacheKey, cacheKey])) {
        console.log(`async operation keyed on ${ cacheKey } will not be repeated`);
        return;
      }
    }

    const modifies = {
      [inflightKey]: "discard",
    };
    if (inflightCacheKey) {
      modifies[inflightCacheKey] = "deep+check";
    }
    dispatch(inline(modifies, state => {
      state[inflightKey] = true;
      if (inflightCacheKey) {
        _.set(state, [inflightCacheKey, cacheKey], true);
      }
    }));

    // await sleep(500);

    try {
      return await body(dispatch, getState);
    } finally {
      try {
        dispatch(inline(inflightKey, state => {
          let inflightOver = true;
          if (inflightCacheKey) {
            const inflightCache = state[inflightCacheKey];
            if (inflightCache) {
              delete inflightCache[cacheKey];
            }
            if (_.size(inflightCache) > 0) {
              inflightOver = false;
            }
          }
          if (inflightOver) {
            delete state[inflightKey];
          }
        }));
      } catch(e) {
        setTimeout(() => {
          throw e;
        });
      }
    }
  };
}

/**
 * @summary Selector-Factory für bequem injektierbare Selectors
 * @desc
 * derived() ist grundsätzlich auf der [reselect]{@link https://github.com/reactjs/reselect} Bibliothek basiert.
 *
 * Mit derived() konstruierten Selectors können unmittelbar als Argumenten für \@store verwendet werden.
 * @param  {String} propName Name der injektierten Eigenschaft
 * @param  {Selectors...} args Für reselect geeignete Parameter-Liste.
 * @return {Selector}
 *
 * @example
 * // ein Selektor, der alle persönliche Namen aus dem Store in einem Array liefert.
 * const personNameListSelector = derived('personNameList',
 *  (state, props) => state.personList, // state ist das Redux-State, props sind die Component-Eigenschaften
 *  personList => _.map(personList, p => p.p_name)
 *  );
 *
 * \@store(personNameListSelector)
 * class PersonNameListPage extends Component {
 *   render() {
 *     const {personNameList} = this.props;
 *     return <div>u.s.w.</div>;
 *   }
 * }
 */
export function derived(propName, ...args) {
  if (args.length === 1) {
    args[0].__property = propName;
    return args[0];
  }
  const f = createSelector.apply(null, args);
  f.__property = propName;
  return f;
}

export function asyncDebounce(modifiedKey, dispatch) {
  if (!dispatch) {
    dispatch = modifiedKey;
    modifiedKey = undefined;
  }

  let waitingBody = null;
  let running = false;

  async function run(body) {
    if (running) {
      waitingBody = body;
      return;
    }
    waitingBody = null;
    running = true;


    try {
      await dispatch(modifiedKey ? asyncOperation(modifiedKey, body) : body);
    } finally {
      running = false;
      if (waitingBody) {
        run(waitingBody);
      }
    }
  }

  return run;
}
